// =============================================================================
// Admin Reconciliation Page — drift list + manual run trigger
// -----------------------------------------------------------------------------
// Shows the recent reconciliation activity (run summaries + drift entries)
// the worker writes to AuditLog and lets the operator manually kick off a
// scan with a tunable window. The trigger always emits its own audit-log
// row server-side.
// =============================================================================

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useApiClient } from '@tkpod/api-client';
import { Button, Card, CardHeader, Modal, ModalFooter, Spinner } from '@tkpod/ui';

interface ReconcileAuditEntry {
  id: string;
  action: string;
  actorType: string;
  actorId: string | null;
  entityType: string | null;
  entityId: string | null;
  tenantId: string | null;
  metadata: Record<string, unknown> | null;
  createdAt: string;
}

interface ReconcileAuditResponse {
  items: ReconcileAuditEntry[];
  limit: number;
}

interface ReconcileRunResponse {
  scannedEvents: number;
  matched: number;
  driftWalletCredits: Array<Record<string, unknown>>;
  driftRefunds: Array<Record<string, unknown>>;
}

const ACTION_LABELS: Record<string, string> = {
  'reconcile.run': 'Run summary',
  'reconcile.manual_trigger': 'Manual trigger',
  'reconcile.drift_wallet_credit': 'Wallet credit drift',
  'reconcile.drift_refund': 'Refund drift',
};

function actionTone(action: string): string {
  if (action.includes('drift')) return 'bg-rose-50 text-rose-700';
  if (action.includes('manual')) return 'bg-sky-50 text-sky-700';
  return 'bg-emerald-50 text-emerald-700';
}

export function AdminReconciliationPage() {
  const api = useApiClient();
  const queryClient = useQueryClient();
  const [runOpen, setRunOpen] = useState(false);
  const [lastRun, setLastRun] = useState<ReconcileRunResponse | null>(null);

  const auditQuery = useQuery<ReconcileAuditResponse>({
    queryKey: ['admin', 'reconciliation', 'audit'],
    queryFn: () =>
      api.get<ReconcileAuditResponse>(
        '/api/v1/admin/reconciliation/audit?limit=100',
      ),
    refetchInterval: 30_000,
  });

  const runMutation = useMutation({
    mutationFn: (body: { windowHours?: number; maxEvents?: number }) =>
      api.post<ReconcileRunResponse>('/api/v1/admin/reconciliation/run', body),
    onSuccess: (data) => {
      setLastRun(data);
      setRunOpen(false);
      queryClient.invalidateQueries({
        queryKey: ['admin', 'reconciliation', 'audit'],
      });
    },
  });

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader title="Reconciliation control" />
        <div className="space-y-3 px-4 pb-5 text-sm sm:px-5">
          <p className="text-text-secondary">
            The reconciliation worker runs nightly and compares the local
            ledger / refund tables against Stripe events. Use the manual
            trigger when you need to verify a fix immediately.
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <Button onClick={() => setRunOpen(true)} disabled={runMutation.isPending}>
              {runMutation.isPending ? 'Running…' : 'Run reconciliation now'}
            </Button>
            <Button
              variant="secondary"
              onClick={() => auditQuery.refetch()}
              disabled={auditQuery.isFetching}
            >
              Refresh log
            </Button>
          </div>
          {lastRun && (
            <div className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-900">
              Last manual run scanned {lastRun.scannedEvents} events •
              matched {lastRun.matched} • drift credits{' '}
              {lastRun.driftWalletCredits.length} • drift refunds{' '}
              {lastRun.driftRefunds.length}
            </div>
          )}
          {runMutation.error && (
            <div className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-900">
              {runMutation.error instanceof Error
                ? runMutation.error.message
                : 'Reconciliation run failed.'}
            </div>
          )}
        </div>
      </Card>

      <Card padding="none">
        <div className="flex items-center justify-between border-b border-border px-5 py-3">
          <h2 className="text-base font-semibold text-text-primary">
            Recent activity
          </h2>
          <p className="text-xs text-text-tertiary">
            Last {auditQuery.data?.limit ?? 100} events, newest first
          </p>
        </div>
        {auditQuery.isLoading ? (
          <div className="flex h-32 items-center justify-center">
            <Spinner />
          </div>
        ) : auditQuery.isError ? (
          <p className="px-5 py-6 text-sm text-rose-600">
            {auditQuery.error instanceof Error
              ? auditQuery.error.message
              : 'Unable to load audit log.'}
          </p>
        ) : !auditQuery.data?.items.length ? (
          <p className="px-5 py-12 text-center text-sm text-text-secondary">
            No reconciliation activity yet — the first nightly run hasn't
            fired or no events have been scanned.
          </p>
        ) : (
          <table className="w-full text-left text-sm">
            <thead className="border-b border-border bg-surface-tertiary text-xs uppercase tracking-wide text-text-tertiary">
              <tr>
                <th className="px-5 py-2 font-semibold">When</th>
                <th className="px-5 py-2 font-semibold">Action</th>
                <th className="px-5 py-2 font-semibold">Actor</th>
                <th className="px-5 py-2 font-semibold">Tenant</th>
                <th className="px-5 py-2 font-semibold">Details</th>
              </tr>
            </thead>
            <tbody>
              {auditQuery.data.items.map((entry) => (
                <tr key={entry.id} className="border-b border-border/60">
                  <td className="px-5 py-3 align-top text-xs text-text-secondary">
                    {new Date(entry.createdAt).toLocaleString()}
                  </td>
                  <td className="px-5 py-3 align-top">
                    <span
                      className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide ${actionTone(
                        entry.action,
                      )}`}
                    >
                      {ACTION_LABELS[entry.action] ?? entry.action}
                    </span>
                  </td>
                  <td className="px-5 py-3 align-top text-xs text-text-secondary">
                    {entry.actorType}
                    {entry.actorId ? ` · ${entry.actorId}` : ''}
                  </td>
                  <td className="px-5 py-3 align-top text-xs text-text-secondary">
                    {entry.tenantId ?? '—'}
                  </td>
                  <td className="px-5 py-3 align-top text-xs text-text-secondary">
                    <pre className="max-w-xl whitespace-pre-wrap break-all font-mono text-[10px] text-text-tertiary">
                      {entry.metadata
                        ? JSON.stringify(entry.metadata, null, 2)
                        : '—'}
                    </pre>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      <RunReconcileModal
        open={runOpen}
        onClose={() => setRunOpen(false)}
        onSubmit={(body) => runMutation.mutate(body)}
        loading={runMutation.isPending}
        error={
          runMutation.error instanceof Error ? runMutation.error.message : null
        }
      />
    </div>
  );
}

function RunReconcileModal({
  open,
  onClose,
  onSubmit,
  loading,
  error,
}: {
  open: boolean;
  onClose: () => void;
  onSubmit: (body: { windowHours?: number; maxEvents?: number }) => void;
  loading: boolean;
  error: string | null;
}) {
  const [windowHours, setWindowHours] = useState('24');
  const [maxEvents, setMaxEvents] = useState('500');
  const [validation, setValidation] = useState<string | null>(null);

  function handleSubmit() {
    setValidation(null);
    const wh = Number.parseInt(windowHours, 10);
    const me = Number.parseInt(maxEvents, 10);
    if (!Number.isFinite(wh) || wh < 1 || wh > 720) {
      setValidation('Window hours must be between 1 and 720 (30 days).');
      return;
    }
    if (!Number.isFinite(me) || me < 1 || me > 10_000) {
      setValidation('Max events must be between 1 and 10,000.');
      return;
    }
    onSubmit({ windowHours: wh, maxEvents: me });
  }

  return (
    <Modal
      open={open}
      onClose={loading ? () => {} : onClose}
      title="Run reconciliation"
      description="Scans Stripe events in the chosen window and compares against the local ledger / refund tables. Drift entries are written to the audit log."
      size="sm"
    >
      <div className="space-y-3">
        <div>
          <label
            htmlFor="rec-hours"
            className="mb-1 block text-xs font-medium uppercase tracking-wide text-text-tertiary"
          >
            Window (hours)
          </label>
          <input
            id="rec-hours"
            type="number"
            min={1}
            max={720}
            value={windowHours}
            onChange={(e) => setWindowHours(e.target.value)}
            disabled={loading}
            className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm focus:border-brand-600 focus:outline-none"
          />
        </div>
        <div>
          <label
            htmlFor="rec-max"
            className="mb-1 block text-xs font-medium uppercase tracking-wide text-text-tertiary"
          >
            Max events to inspect
          </label>
          <input
            id="rec-max"
            type="number"
            min={1}
            max={10_000}
            value={maxEvents}
            onChange={(e) => setMaxEvents(e.target.value)}
            disabled={loading}
            className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm focus:border-brand-600 focus:outline-none"
          />
        </div>
        {(validation || error) && (
          <div className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-900">
            {validation ?? error}
          </div>
        )}
      </div>
      <ModalFooter>
        <Button variant="ghost" onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} disabled={loading}>
          {loading ? 'Running…' : 'Run scan'}
        </Button>
      </ModalFooter>
    </Modal>
  );
}
